import { FlexBox, Spinner, Text } from '@dixa/lib-shared-habla';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useEffect } from 'react';
import { useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { searchApi } from 'apis/search';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { pubSub } from 'utils/pubSub';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ContactsUser } from './components/user/contactsUser';

function ContactListMeta({ children }: PropsWithChildren) {
  return (
    <FlexBox justifyContent="center" alignItems="center" style={{ height: 72 }}>
      {children}
    </FlexBox>
  );
}

export function ContactsUsers(): JSX.Element {
  const { id } = useAppPathParams('/contacts/user/:id');

  const {
    params: { q: searchTerm = '' },
  } = useAppSearchParams('/contacts/user/:id');

  const {
    items: contacts,
    asyncStatus,
    nextPage,
    refetchItems,
    search,
    hasNextPage,
    updateItem,
  } = usePaginatedEndpoint({
    endpoint: searchApi.endpoints.readSearchUser,
    itemsSelector: ({ data }) => data.results.hits,
    infinite: true,
    initialArg: { limit: 50, offset: 0 },
  });

  useUpdateEffect(() => {
    search(searchTerm);
  }, [searchTerm, search]);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe('userModalUserAdded', () => {
      // Delaying refetch so posgress has time
      // to provision the dynamoDB database
      setTimeout(() => {
        refetchItems();
      }, 2000);
    });
    return () => unsubscribe();
  }, [refetchItems]);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe(
      'mergeContactsModalUserMerged',
      (user) => {
        const contact = contacts.find((contact) => contact.id === user.id);

        if (contact) {
          updateItem(
            {
              ...contact,
              ...(user.email && {
                email: user.email,
              }),
              ...(user.phone_number && {
                phone_number: user.phone_number,
              }),
              ...(user.name && {
                display_name: user.name,
              }),
              ...(user.company_id && {
                company_id: user.company_id,
              }),
              ...(user.avatar_url && {
                avatar_url: user.avatar_url,
              }),
            },
            (a, b) => a.id === b.id,
          );
        }
      },
    );
    return () => unsubscribe();
  }, [contacts, updateItem]);

  if (asyncStatus === 'rejected') {
    return (
      <ContactListMeta>
        <Text.Paragraph>
          An error occurred while getting users list
        </Text.Paragraph>
      </ContactListMeta>
    );
  }

  if (asyncStatus === 'pending' && contacts.length === 0) {
    return (
      <ContactListMeta>
        <Spinner />
      </ContactListMeta>
    );
  }

  return (
    <>
      {contacts.map((user) => (
        <ContactsUser key={user.id} user={user} selected={user.id === id} />
      ))}

      {asyncStatus === 'pending' && (
        <ContactListMeta>
          <Spinner />
        </ContactListMeta>
      )}

      {asyncStatus === 'fulfilled' && hasNextPage && (
        <Waypoint onEnter={nextPage} />
      )}
    </>
  );
}
